
import { inject, NgModule, InjectionToken, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AgGridModule } from 'ag-grid-angular';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTabsModule } from '@angular/material/tabs';
import { FgaModule } from './fga/fga.module';
import { CommonModule } from '@angular/common';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './core/header/header.component';
import { FooterComponent } from './core/footer/footer.component';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ToolbarComponent } from './core/toolbar/toolbar.component';
import { CreateComponent } from './main/create/create.component';
import { StatsComponent } from './main/stats/stats.component';
import { NgxUiLoaderModule, NgxUiLoaderConfig, SPINNER, POSITION, PB_DIRECTION } from "ngx-ui-loader";
import { RoutesComponent } from './main/routes/routes.component';

import { MsalService, MSAL_INSTANCE, MsalGuard, MsalInterceptor, MsalBroadcastService } from './msal';
import { IPublicClientApplication, PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG } from './msal/constants';
import { MsalInterceptorConfig } from './msal/msal.interceptor.config';
import { environment } from 'src/environments/environment';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { MasterDetailModule } from 'ag-grid-enterprise';
ModuleRegistry.registerModules([
    AllCommunityModule,
    MasterDetailModule
]);

import {
    MsalInterceptorConfiguration,
    MsalGuardConfiguration,
} from '@azure/msal-angular';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FgaService } from './fga/fga.service';
import { UnauthorizedComponent } from './main/unauthorized/unauthorized.component';
import { provideApollo } from 'apollo-angular';
import { InMemoryCache } from '@apollo/client/cache';
import { AddParamsInterceptor } from './add-params.interceptor';
import { BreadcrumbComponent } from './core/breadcrumb/breadcrumb.component';
export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: environment.CLIENT_ID,
            authority: environment.TENANT_ID,
            redirectUri: environment.REDIRECT_URL
        }
    });
}



export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set(
        environment.REDIRECT_URL,
        environment.SCOPES
    );

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [...environment.SCOPES],
        },
        loginFailedRoute: '/unauthorized',
    };
}

export function initializeMsal(instance: IPublicClientApplication): () => Promise<void> {
    return () => instance.initialize();
}

export const FGA_API_ENDPOINT = new InjectionToken<string>('FGA_API_ENDPOINT');

const ctr = "center-center"
const ngxUiLoaderConfig: NgxUiLoaderConfig = {
    "bgsColor": "red",
    "bgsOpacity": 0.5,
    "bgsPosition": "bottom-right",
    "bgsSize": 60,
    "bgsType": "ball-spin-clockwise",
    "blur": 5,
    "delay": 0,
    "fastFadeOut": true,
    "fgsColor": "red",
    "fgsPosition": ctr,
    "fgsSize": 60,
    "fgsType": "ball-spin-clockwise",
    "gap": 24,
    "logoPosition": ctr,
    "logoSize": 120,
    "logoUrl": "",
    "masterLoaderId": "master",
    "overlayBorderRadius": "0",
    "overlayColor": "rgba(40, 40, 40, 0.8)",
    "pbColor": "red",
    "pbDirection": "ltr",
    "pbThickness": 3,
    "hasProgressBar": true,
    "text": "",
    "textColor": "#FFFFFF",
    "textPosition": ctr,
    "maxTime": -1,
    "minTime": 300
}

@NgModule({
    declarations: [
        AppComponent,
        HeaderComponent,
        FooterComponent,
        ToolbarComponent,
        CreateComponent,
        StatsComponent,
        RoutesComponent,
        UnauthorizedComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        FormsModule,
        ReactiveFormsModule,
        MatListModule,
        MatMenuModule,
        MatIconModule,
        MatDialogModule,
        MatSlideToggleModule,
        MatDatepickerModule,
        AgGridModule,
        BrowserAnimationsModule,
        NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
        MatFormFieldModule,
        MatSelectModule,
        NgxMatSelectSearchModule,
        MatNativeDateModule,
        MatInputModule,
        MatCheckboxModule,
        FormsModule,
        MatTabsModule,
        FgaModule,
        CommonModule,
        BreadcrumbComponent], providers: [
            {
                provide: HTTP_INTERCEPTORS,
                useClass: MsalInterceptor,
                multi: true
            },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: AddParamsInterceptor,
                multi: true
            },
            {
                provide: MSAL_INSTANCE,
                useFactory: MSALInstanceFactory
            },
            provideAppInitializer(() => {
                const msalInstance = inject(MSAL_INSTANCE);
                return msalInstance.initialize();
            }),
            {
                provide: MSAL_GUARD_CONFIG,
                useFactory: MSALGuardConfigFactory
            },
            {
                provide: MSAL_INTERCEPTOR_CONFIG,
                useFactory: MSALInterceptorConfigFactory
            },
            MsalService,
            MsalGuard,
            MsalBroadcastService,
            provideHttpClient(withInterceptorsFromDi()),
            provideApollo(() => {
                return { cache: new InMemoryCache() }
            }),
            { provide: FGA_API_ENDPOINT, useValue: environment.APM_DS_URI }
        ]
})

export class AppModule { }
